/**
 * Bundle Builder
 * Manages bundle state, tab switching with AJAX collection loading,
 * filter interception, sidebar panel, and cart integration.
 */
(function () {
  'use strict';

  const CONFIG = window.bundleBuilderConfig || {};
  const TIERS = (CONFIG.tiers || []).sort((a, b) => a.min_items - b.min_items);
  const COLLECTIONS = CONFIG.collections || [];
  const MONEY_FORMAT = CONFIG.moneyFormat || '€{{amount}}';
  const STORAGE_KEY = 'bb_bundle_' + CONFIG.sectionId;
  const FREE_TIERS = (CONFIG.freeTiers || []).sort((a, b) => a.min_items - b.min_items);

  // Locale-aware root ("/", "/de/", "/fr-ca/"…) so AJAX-loaded collections and
  // filters return content in the active language, not the default locale.
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ══════════════════════════════════════════════════════════════
     STATE
  ══════════════════════════════════════════════════════════════ */

  // Bundle items: { variantId: { productId, variantId, title, price, image, qty, url, tabIndex } }
  let bundle = {};
  let activeTabIndex = 0;
  let loadedTabs = new Set();

  // Restore from sessionStorage
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) bundle = JSON.parse(saved);
  } catch (e) { /* ignore */ }

  function saveState() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bundle));
    } catch (e) { /* ignore */ }
  }

  /* ══════════════════════════════════════════════════════════════
     HELPERS
  ══════════════════════════════════════════════════════════════ */

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return MONEY_FORMAT
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount}}', amount)
      .replace('{{amount_with_space_separator}}', amount.replace(/\B(?=(\d{3})+(?!\d))/g, ' '))
      .replace('{{amount_no_decimals_with_space_separator}}', Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function totalItems() {
    return Object.values(bundle).reduce((sum, item) => sum + item.qty, 0);
  }

  function totalPrice() {
    return Object.values(bundle).reduce((sum, item) => sum + item.price * item.qty, 0);
  }

  function currentTier() {
    const count = totalItems();
    let tier = null;
    for (const t of TIERS) {
      if (count >= t.min_items) tier = t;
    }
    return tier;
  }

  function nextTier() {
    const count = totalItems();
    for (const t of TIERS) {
      if (count < t.min_items) return t;
    }
    return null;
  }

  function itemsPerTab(tabIndex) {
    return Object.values(bundle)
      .filter(item => item.tabIndex === tabIndex)
      .reduce((sum, item) => sum + item.qty, 0);
  }

  function currentFreeTier() {
    const count = totalItems();
    let tier = null;
    for (const t of FREE_TIERS) {
      if (count >= t.min_items) tier = t;
    }
    return tier;
  }

  function nextFreeTier() {
    const count = totalItems();
    for (const t of FREE_TIERS) {
      if (count < t.min_items) return t;
    }
    return null;
  }

  function cheapestItems(n) {
    return Object.values(bundle).slice().sort((a, b) => a.price - b.price).slice(0, n);
  }

  function maxFreeTierThreshold() {
    if (FREE_TIERS.length === 0) return 0;
    return FREE_TIERS[FREE_TIERS.length - 1].min_items;
  }

  function minRequired() {
    return TIERS.length > 0 ? TIERS[0].min_items : 1;
  }

  /**
   * Calculate totals, savings, and free item info — single source of truth
   * used by both desktop renderSubtotal() and mobile renderMobileTray().
   */
  function calculateSavings() {
    const rawTotal = totalPrice();
    const tier = currentTier();
    const freeTier = currentFreeTier();

    let freeSavings = 0;
    let freeItems = [];
    if (freeTier) {
      freeItems = cheapestItems(freeTier.free_count);
      for (const item of freeItems) freeSavings += item.price;
    }

    const discountPercent = tier ? tier.discount_percent : 0;
    const afterFree = rawTotal - freeSavings;
    const percentSavings = Math.round(afterFree * discountPercent / 100);
    const totalSavings = freeSavings + percentSavings;
    const finalTotal = rawTotal - totalSavings;

    return { rawTotal, freeTier, freeSavings, freeItems, discountPercent, totalSavings, finalTotal };
  }

  /**
   * Build subtotal HTML — shared between desktop panel and mobile tray.
   */
  function buildSubtotalHtml(calc) {
    let html = `<span class="bb-subtotal__label">${escapeHtml(CONFIG.subtotalLabel)}</span>`;

    if (calc.rawTotal <= 0) {
      return html + '<span class="bb-subtotal__value">\u2014</span>';
    }

    if (calc.totalSavings > 0) {
      html += `
        <div class="bb-subtotal__prices">
          <span class="bb-subtotal__original">${formatMoney(calc.rawTotal)}</span>
          <span class="bb-subtotal__value">${formatMoney(calc.finalTotal)}</span>
        </div>`;
      let savingsText = '';
      if (calc.discountPercent > 0) savingsText += '-' + calc.discountPercent + '%';
      if (calc.freeSavings > 0) {
        if (savingsText) savingsText += ' + ';
        savingsText += calc.freeTier.free_count + ' ' + CONFIG.freeLabel + (calc.freeTier.free_count > 1 ? 's' : '');
      }
      html += `<div class="bb-subtotal__savings">${escapeHtml(CONFIG.savingsPrefix)} ${formatMoney(calc.totalSavings)} <span class="bb-subtotal__savings-detail">(${savingsText})</span></div>`;
    } else {
      html += `<span class="bb-subtotal__value">${formatMoney(calc.rawTotal)}</span>`;
    }

    return html;
  }

  /**
   * Build bundle item HTML — shared between desktop list and mobile tray.
   * @param {Object} item - A bundle item
   * @param {Set} freeIds - Set of variant IDs that are free
   * @param {string} dataPrefix - data attribute prefix ('bundle-item' for desktop, 'mobile-bundle-item' for mobile)
   * @param {string} qtyAttr - data attribute for qty buttons ('qty-change' for desktop, 'mobile-qty' for mobile)
   * @param {string} removeAttr - data attribute for remove button ('remove-item' for desktop, 'mobile-remove' for mobile)
   */
  function buildItemHtml(item, freeIds, dataPrefix, qtyAttr, removeAttr) {
    const isFree = freeIds.has(item.variantId);
    const itemClass = 'bb-item' + (isFree ? ' bb-item--free' : '');
    const priceHtml = isFree
      ? `<span class="bb-item__badge-free">${escapeHtml(CONFIG.freeBadgeText)}</span><span class="bb-item__price bb-item__price--original">${formatMoney(item.price * item.qty)}</span><span class="bb-item__price">${formatMoney(0)}</span>`
      : `<span class="bb-item__price">${formatMoney(item.price * item.qty)}</span>`;

    return `
      <div class="${itemClass}" data-${dataPrefix}="${item.variantId}">
        <img class="bb-item__img" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" width="52" height="52" loading="lazy">
        <div class="bb-item__info">
          <div class="bb-item__name">${escapeHtml(item.title)}</div>
          <div class="bb-item__qty">
            <button class="bb-item__qty-btn" type="button" data-${qtyAttr}="${item.variantId}" data-delta="-1" aria-label="${escapeHtml(CONFIG.decreaseLabel || 'Decrease quantity')}">\u2212</button>
            <span class="bb-item__qty-val">${item.qty}</span>
            <button class="bb-item__qty-btn" type="button" data-${qtyAttr}="${item.variantId}" data-delta="1" aria-label="${escapeHtml(CONFIG.increaseLabel || 'Increase quantity')}">+</button>
          </div>
        </div>
        <div class="bb-item__right">
          ${priceHtml}
          <button class="bb-item__remove" type="button" data-${removeAttr}="${item.variantId}" aria-label="${escapeHtml(CONFIG.removeLabel || 'Remove')} ${escapeHtml(item.title)}">\u00d7</button>
        </div>
      </div>`;
  }

  /**
   * Get the nudge/progress pill text for the current state.
   */
  function getNudgeText() {
    const count = totalItems();
    const next = nextTier();
    const nFree = nextFreeTier();
    const activeFree = currentFreeTier();

    if (next && count > 0) {
      const remaining = next.min_items - count;
      return CONFIG.nudgeTemplate.replace('{count}', remaining).replace('{discount}', next.discount_percent);
    }
    if (nFree && count > 0) {
      return nFree.nudge.replace('{count}', nFree.min_items - count);
    }
    if (activeFree) {
      return activeFree.label;
    }
    return '';
  }

  /* ══════════════════════════════════════════════════════════════
     DOM REFERENCES
  ══════════════════════════════════════════════════════════════ */

  const section = document.querySelector('[data-bundle-builder]');
  if (!section) return;

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  const bundleList = $('[data-bundle-list]');
  const bundleEmpty = $('[data-bundle-empty]');
  const subtotalEl = $('[data-subtotal]');
  const atcBtn = $('[data-add-bundle-to-cart]');
  const clearBtn = $('[data-clear-bundle]');
  const progressText = $('[data-progress-text]');
  const progressFill = $('[data-progress-fill]');
  const toastsContainer = $('[data-toasts]');

  // Mobile
  const mobileTray = $('[data-mobile-tray]');
  const mobileTrayToggle = $('[data-mobile-tray-toggle]');
  const mobileTrayBody = $('[data-mobile-tray-body]');
  const mobileTrayPill = $('[data-mobile-tray-pill]');

  // Nudge
  const desktopNudge = $('[data-nudge]');
  const desktopNudgeText = $('[data-nudge-text]');
  const mobileNudge = $('[data-mobile-nudge]');
  const mobileNudgeText = $('[data-mobile-nudge-text]');

  // Counts
  const productCount = $('[data-product-count]');
  const productCountDesktop = $('[data-product-count-desktop]');

  /* ══════════════════════════════════════════════════════════════
     TABS — switching + AJAX loading
  ══════════════════════════════════════════════════════════════ */

  // Mark first tab as loaded
  if (COLLECTIONS.length > 0) {
    loadedTabs.add(0);
  }

  function switchTab(index) {
    if (index === activeTabIndex && loadedTabs.has(index)) return;
    activeTabIndex = index;

    // Update tab active states
    $$('.bb-tab').forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });

    // Update panel visibility
    $$('.bb-tab-panel').forEach((panel, i) => {
      panel.classList.toggle('active', i === index);
    });

    // Load products if not yet loaded
    if (!loadedTabs.has(index)) {
      loadCollectionDirect(index);
    } else {
      updateProductCount();
    }

    // Update filters sidebar for this collection
    loadFilters(index);

    // Update next-tab button label
    updateNextTabButton();
  }

  let collectionAbortController = null;

  async function loadCollectionDirect(tabIndex, filterQuery) {
    const col = COLLECTIONS[tabIndex];
    if (!col || !col.handle) return;

    const panel = $$('.bb-tab-panel')[tabIndex];
    const grid = $('[data-product-grid]', panel);
    if (!grid) return;

    // Cancel any in-flight collection fetch
    if (collectionAbortController) collectionAbortController.abort();
    collectionAbortController = new AbortController();

    let url = `${ROOT}collections/${col.handle}?view=bundle-grid`;
    if (filterQuery) {
      url += '&' + filterQuery;
    }

    try {
      const response = await fetch(url, { signal: collectionAbortController.signal });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-product-grid]');

      if (newGrid) {
        grid.innerHTML = newGrid.innerHTML;
        const emptyEl = grid.querySelector('[data-empty-collection]');
        if (emptyEl) emptyEl.textContent = CONFIG.noResultsText;
      } else {
        grid.innerHTML = '<div class="bb-grid-empty">' + escapeHtml(CONFIG.noResultsText) + '</div>';
      }

      loadedTabs.add(tabIndex);
      bindCardButtons(grid);
      syncCardStates();
      updateProductCount();
    } catch (err) {
      if (err.name === 'AbortError') return;
      grid.innerHTML = '<div class="bb-grid-empty">' + escapeHtml(CONFIG.errorLoadText) + '</div>';
    }
  }

  async function loadFilters(tabIndex) {
    const col = COLLECTIONS[tabIndex];
    if (!col || !col.handle) return;

    const filtersContainer = $('[data-bundle-filters]');
    if (!filtersContainer) return;

    try {
      const response = await fetch(`${ROOT}collections/${col.handle}?view=bundle-filters`);
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newFilters = doc.querySelector('[data-bundle-filters]');

      if (newFilters) {
        filtersContainer.innerHTML = newFilters.innerHTML;
        bindFilterForm();
      }
    } catch (err) {
      // Keep existing filters
    }
  }

  /* ══════════════════════════════════════════════════════════════
     FILTERS — intercept facets form
  ══════════════════════════════════════════════════════════════ */

  let filterDebounceTimer = null;

  function bindFilterForm() {
    const filtersContainer = $('[data-bundle-filters]');
    if (!filtersContainer) return;

    const form = filtersContainer.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      applyFilters(form);
    });

    form.addEventListener('change', () => {
      updateActiveFilters();
      clearTimeout(filterDebounceTimer);
      filterDebounceTimer = setTimeout(() => applyFilters(form), 250);
    });

    updateActiveFilters();
  }

  /* ──────────────────────────────────────────────────────────────
     Active-filter chips + per-accordion "N Selected" badges
  ────────────────────────────────────────────────────────────── */

  function getActiveFilterInputs() {
    const filtersContainer = $('[data-bundle-filters]');
    if (!filtersContainer) return [];

    const active = [];
    // Checkboxes / radios that are active filter controls
    filtersContainer.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      if (!input.name || !input.name.startsWith('filter.')) return;
      if (!input.checked) return;
      active.push(input);
    });
    return active;
  }

  function getFilterLabel(input) {
    // Try associated <label>; fall back to input title/value. Strip trailing "(123)" count.
    let label = '';
    if (input.id) {
      const lbl = input.ownerDocument.querySelector('label[for="' + CSS.escape(input.id) + '"]');
      if (lbl) label = lbl.textContent || '';
    }
    if (!label) label = input.getAttribute('aria-label') || input.title || input.value || '';
    return label.replace(/\s*\(\d[\d\s.,]*\)\s*$/, '').trim();
  }

  function formatFilterCount(count) {
    const template = CONFIG.filterCountTemplate || '{count} selected';
    return template
      .replace('{count}', count)
      .replace('{plural}', count > 1 ? 's' : '');
  }

  function updateActiveFilters() {
    const filtersContainer = $('[data-bundle-filters]');
    if (!filtersContainer) return;

    const activeInputs = getActiveFilterInputs();
    const chipContainer = $('[data-active-filters-chips]');
    const wrap = $('[data-active-filters]');

    // Build chip list
    if (chipContainer && wrap) {
      if (activeInputs.length === 0) {
        chipContainer.innerHTML = '';
        wrap.hidden = true;
      } else {
        chipContainer.innerHTML = activeInputs.map((input, i) => {
          const label = getFilterLabel(input);
          return (
            '<button type="button" class="bb-filter-chip" data-chip-index="' + i + '">' +
              '<span class="bb-filter-chip__label">' + escapeHtml(label) + '</span>' +
              '<svg class="bb-filter-chip__x" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>' +
            '</button>'
          );
        }).join('');
        wrap.hidden = false;

        // Wire chip removal
        chipContainer.querySelectorAll('.bb-filter-chip').forEach((chip) => {
          chip.addEventListener('click', () => {
            const idx = parseInt(chip.dataset.chipIndex, 10);
            const target = activeInputs[idx];
            if (target) {
              target.checked = false;
              target.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      }
    }

    // Inject per-accordion "N Selected" counts
    filtersContainer.querySelectorAll('details.accordion').forEach((accordion) => {
      const activeInside = accordion.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').length;

      // Remove existing badge
      const existing = accordion.querySelector('.bb-filter-count');
      if (existing) existing.remove();

      if (activeInside > 0) {
        const toggle = accordion.querySelector('.accordion__toggle');
        if (!toggle) return;
        const badge = document.createElement('span');
        badge.className = 'bb-filter-count';
        badge.textContent = formatFilterCount(activeInside);
        // Insert before the chevron svg if present, else append
        const svg = toggle.querySelector('svg');
        if (svg) toggle.insertBefore(badge, svg);
        else toggle.appendChild(badge);
      }
    });
  }

  function applyFilters(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (value) params.append(key, value);
    }

    params.delete('page');
    loadCollectionDirect(activeTabIndex, params.toString());
  }

  // Bind initial filter form
  bindFilterForm();

  // Mobile filter drawer — show sidebar as overlay
  const mobileFilterBtn = $('[data-open-mobile-filters]');
  const sidebar = section.querySelector('.bb-sidebar');
  if (mobileFilterBtn && sidebar) {
    mobileFilterBtn.addEventListener('click', () => {
      sidebar.classList.toggle('bb-sidebar--open');
    });

    // Close when tapping outside the sidebar
    document.addEventListener('click', (e) => {
      if (sidebar.classList.contains('bb-sidebar--open') && !sidebar.contains(e.target) && !mobileFilterBtn.contains(e.target)) {
        sidebar.classList.remove('bb-sidebar--open');
      }
    });
  }

  // Close filters button (in filter panel header) — closes mobile drawer
  if (sidebar) {
    sidebar.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-close-filters]');
      if (!closeBtn) return;
      sidebar.classList.remove('bb-sidebar--open');
    });
  }

  /* ══════════════════════════════════════════════════════════════
     TAB EVENTS
  ══════════════════════════════════════════════════════════════ */

  $$('.bb-tab').forEach((tab, index) => {
    tab.addEventListener('click', () => switchTab(index));
  });

  // Next-tab buttons (desktop + mobile)
  const nextTabBtns = $$('[data-next-tab]');
  const nextTabLabels = $$('[data-next-tab-label]');

  function updateNextTabButton() {
    if (COLLECTIONS.length <= 1) return;
    const nextIndex = (activeTabIndex + 1) % COLLECTIONS.length;
    nextTabLabels.forEach(el => { el.textContent = COLLECTIONS[nextIndex].title; });
  }

  nextTabBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const nextIndex = (activeTabIndex + 1) % COLLECTIONS.length;
      switchTab(nextIndex);
      updateNextTabButton();
    });
  });

  /* ══════════════════════════════════════════════════════════════
     CARD BUTTONS — Add/remove from bundle
  ══════════════════════════════════════════════════════════════ */

  function bindCardButtons(container) {
    const cards = (container || section).querySelectorAll('.bb-card');
    cards.forEach(card => {
      const cta = card.querySelector('.bb-card__cta');
      if (!cta || cta.disabled) return;

      // Remove old listener by cloning
      const newCta = cta.cloneNode(true);
      cta.parentNode.replaceChild(newCta, cta);

      newCta.addEventListener('click', () => {
        toggleBundleItem(card);
      });

      // Variant select change
      const variantSelect = card.querySelector('.bb-card__variant-select');
      if (variantSelect) {
        variantSelect.addEventListener('change', (e) => {
          const option = e.target.selectedOptions[0];
          card.dataset.variantId = option.value;
          card.dataset.productPrice = option.dataset.price;
          card.dataset.productAvailable = option.dataset.available;

          if (option.dataset.image) {
            card.dataset.productImage = option.dataset.image;
          }

          syncSingleCard(card);
        });
      }
    });
  }

  function toggleBundleItem(card) {
    const variantId = card.dataset.variantId;

    if (bundle[variantId]) {
      delete bundle[variantId];
      toast(CONFIG.toastRemovedText);
    } else {
      bundle[variantId] = {
        productId: card.dataset.productId,
        variantId: variantId,
        title: card.dataset.productTitle,
        price: parseInt(card.dataset.productPrice),
        image: card.dataset.productImage,
        url: card.dataset.productUrl,
        qty: 1,
        tabIndex: activeTabIndex
      };
      toast(CONFIG.toastAddedText);
    }

    saveState();
    render();
  }

  /* ══════════════════════════════════════════════════════════════
     RENDER — update all UI from state
  ══════════════════════════════════════════════════════════════ */

  function render() {
    renderBundleList();
    renderProgress();
    renderSubtotal();
    renderTabBadges();
    renderNudge();
    syncCardStates();
    renderMobileTray();
    updateAtcButton();
    updateProductCount();
  }

  function renderBundleList() {
    const items = Object.values(bundle);

    if (items.length === 0) {
      bundleEmpty.style.display = '';
      $$('.bb-item', bundleList).forEach(el => el.remove());
      return;
    }

    bundleEmpty.style.display = 'none';

    const calc = calculateSavings();
    const freeIds = new Set(calc.freeItems.map(i => i.variantId));

    let html = '';
    for (const item of items) {
      html += buildItemHtml(item, freeIds, 'bundle-item', 'qty-change', 'remove-item');
    }

    $$('.bb-item', bundleList).forEach(el => el.remove());
    bundleEmpty.insertAdjacentHTML('afterend', html);

    // Bind qty buttons
    $$('[data-qty-change]', bundleList).forEach(btn => {
      btn.addEventListener('click', () => {
        const vid = btn.dataset.qtyChange;
        const delta = parseInt(btn.dataset.delta);
        if (bundle[vid]) {
          bundle[vid].qty = Math.max(0, bundle[vid].qty + delta);
          if (bundle[vid].qty === 0) delete bundle[vid];
          saveState();
          render();
        }
      });
    });

    // Bind remove buttons
    $$('[data-remove-item]', bundleList).forEach(btn => {
      btn.addEventListener('click', () => {
        delete bundle[btn.dataset.removeItem];
        toast(CONFIG.toastRemovedText);
        saveState();
        render();
      });
    });
  }

  function renderProgress() {
    const count = totalItems();
    const maxTarget = maxFreeTierThreshold() || (TIERS.length > 0 ? TIERS[TIERS.length - 1].min_items : 1);

    const pct = Math.min(100, (count / maxTarget) * 100);
    if (progressFill) progressFill.style.width = pct + '%';

    // Update discount tier milestones
    $$('[data-milestone]').forEach((ms) => {
      const tier = TIERS[parseInt(ms.dataset.milestone)];
      if (tier) ms.classList.toggle('reached', count >= tier.min_items);
    });

    $$('[data-milestone-label]').forEach((label) => {
      const tier = TIERS[parseInt(label.dataset.milestoneLabel)];
      if (tier) label.classList.toggle('reached', count >= tier.min_items);
    });

    // Update free-item milestones
    $$('[data-milestone-free]').forEach((ms) => {
      const ft = FREE_TIERS[parseInt(ms.dataset.milestoneFree)];
      if (ft) ms.classList.toggle('reached', count >= ft.min_items);
    });

    $$('[data-milestone-label-free]').forEach((label) => {
      const ft = FREE_TIERS[parseInt(label.dataset.milestoneLabelFree)];
      if (ft) label.classList.toggle('reached', count >= ft.min_items);
    });

    // Update progress text
    if (progressText) {
      const next = nextTier();
      const activeFree = currentFreeTier();
      const nFree = nextFreeTier();

      if (activeFree && !nFree) {
        progressText.textContent = activeFree.label;
        progressText.classList.add('reached');
      } else if (nFree && !next) {
        progressText.textContent = nFree.nudge.replace('{count}', nFree.min_items - count);
        progressText.classList.remove('reached');
      } else if (next) {
        progressText.textContent = CONFIG.nudgeTemplate
          .replace('{count}', next.min_items - count)
          .replace('{discount}', next.discount_percent);
        progressText.classList.remove('reached');
      } else {
        progressText.textContent = CONFIG.progressInitialText;
        progressText.classList.remove('reached');
      }
    }
  }

  function renderSubtotal() {
    const subtotalContainer = section.querySelector('.bb-panel .bb-subtotal');
    if (!subtotalContainer) return;

    subtotalContainer.innerHTML = buildSubtotalHtml(calculateSavings());
  }

  function renderTabBadges() {
    COLLECTIONS.forEach((_, i) => {
      const badge = $(`[data-tab-badge="${i}"]`);
      if (!badge) return;

      const count = itemsPerTab(i);
      badge.textContent = count;
      badge.classList.toggle('visible', count > 0);
      badge.setAttribute('aria-label', count + ' ' + (CONFIG.itemsLabel || 'items'));
    });
  }

  function renderNudge() {
    const text = getNudgeText();

    if (text) {
      if (desktopNudge) {
        desktopNudge.classList.add('visible');
        if (desktopNudgeText) desktopNudgeText.textContent = text;
      }
      if (mobileNudge) {
        mobileNudge.classList.add('visible');
        if (mobileNudgeText) mobileNudgeText.textContent = text;
      }
    } else {
      if (desktopNudge) desktopNudge.classList.remove('visible');
      if (mobileNudge) mobileNudge.classList.remove('visible');
    }
  }

  function syncCardStates() {
    $$('.bb-card').forEach(card => syncSingleCard(card));
  }

  function syncSingleCard(card) {
    const variantId = card.dataset.variantId;
    const inBundle = !!bundle[variantId];
    card.classList.toggle('in-bundle', inBundle);

    const cta = card.querySelector('.bb-card__cta');
    if (cta && !cta.disabled) {
      cta.textContent = inBundle ? CONFIG.inBundleLabel : CONFIG.addToBundleLabel;
    }
  }

  function updateAtcButton() {
    if (atcBtn) {
      atcBtn.disabled = totalItems() < minRequired();
    }
  }

  function updateProductCount() {
    const activePanel = $$('.bb-tab-panel')[activeTabIndex];
    if (!activePanel) return;

    const count = activePanel.querySelectorAll('.bb-card').length;
    const text = CONFIG.productsCountTemplate
      ? CONFIG.productsCountTemplate.replace('{count}', count)
      : count + ' ' + CONFIG.itemsLabel;

    if (productCount) productCount.textContent = text;
    if (productCountDesktop) productCountDesktop.textContent = text;
  }

  /* ══════════════════════════════════════════════════════════════
     MOBILE TRAY
  ══════════════════════════════════════════════════════════════ */

  function renderMobileTray() {
    const count = totalItems();
    const calc = calculateSavings();
    const freeIds = new Set(calc.freeItems.map(i => i.variantId));

    // Update pill text
    if (mobileTrayPill) {
      if (count === 0) {
        mobileTrayPill.textContent = '0 ' + CONFIG.itemsLabel;
      } else {
        const text = getNudgeText();
        mobileTrayPill.textContent = text || count + ' ' + CONFIG.itemsLabel;
      }
    }

    // Update discount badge
    const discountBadge = section.querySelector('[data-mobile-tray-discount]');
    if (discountBadge) {
      if (calc.discountPercent > 0) {
        discountBadge.textContent = '-' + calc.discountPercent + '%';
        discountBadge.classList.add('visible');
      } else {
        discountBadge.classList.remove('visible');
      }
    }

    // Update body content — mirror the desktop panel
    if (mobileTrayBody) {
      const items = Object.values(bundle);
      let html = '';

      for (const item of items) {
        html += buildItemHtml(item, freeIds, 'mobile-bundle-item', 'mobile-qty', 'mobile-remove');
      }

      // Footer with subtotal + atc
      const activeFree = currentFreeTier();
      const nFree = nextFreeTier();

      html += `
        <div class="bb-panel__footer" style="padding: 13px 0;">
          <div class="bb-progress">
            <div class="bb-progress__text${activeFree && !nFree ? ' reached' : ''}">${progressText ? progressText.textContent : ''}</div>
          </div>
          <div class="bb-subtotal">
            ${buildSubtotalHtml(calc)}
          </div>
          <button class="bb-atc" type="button" data-mobile-atc ${count < minRequired() ? 'disabled' : ''}>
            ${escapeHtml(CONFIG.addToCartLabel)}
          </button>
        </div>`;

      mobileTrayBody.innerHTML = html;

      // Bind mobile qty/remove
      $$('[data-mobile-qty]', mobileTrayBody).forEach(btn => {
        btn.addEventListener('click', () => {
          const vid = btn.dataset.mobileQty;
          const delta = parseInt(btn.dataset.delta);
          if (bundle[vid]) {
            bundle[vid].qty = Math.max(0, bundle[vid].qty + delta);
            if (bundle[vid].qty === 0) delete bundle[vid];
            saveState();
            render();
          }
        });
      });

      $$('[data-mobile-remove]', mobileTrayBody).forEach(btn => {
        btn.addEventListener('click', () => {
          delete bundle[btn.dataset.mobileRemove];
          toast(CONFIG.toastRemovedText);
          saveState();
          render();
        });
      });

      // Bind mobile ATC
      const mobileAtc = $('[data-mobile-atc]', mobileTrayBody);
      if (mobileAtc) {
        mobileAtc.addEventListener('click', addBundleToCart);
      }
    }
  }

  // Toggle mobile tray
  if (mobileTrayToggle) {
    mobileTrayToggle.addEventListener('click', () => {
      const isOpen = mobileTray.classList.toggle('open');
      mobileTrayToggle.setAttribute('aria-expanded', isOpen);
    });
  }

  /* ══════════════════════════════════════════════════════════════
     ADD TO CART
  ══════════════════════════════════════════════════════════════ */

  async function addBundleToCart() {
    const items = Object.values(bundle);
    if (items.length === 0) return;

    const allAtcBtns = $$('[data-add-bundle-to-cart], [data-mobile-atc]');
    allAtcBtns.forEach(btn => {
      btn.disabled = true;
      btn.textContent = CONFIG.addingLabel;
    });

    const bundleId = 'bb_' + Date.now();
    const cartItems = items.map(item => ({
      id: parseInt(item.variantId),
      quantity: item.qty,
      properties: {
        '_bundle_id': bundleId,
        [CONFIG.bundlePropertyName]: bundleId
      }
    }));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      });

      if (!response.ok) throw new Error('Cart error');

      // Capture the reached tier's discount code BEFORE clearing the bundle —
      // currentTier() derives the item count from `bundle`, which is wiped below.
      const reached = currentTier();
      const code = reached && reached.code ? String(reached.code).trim() : '';

      bundle = {};
      saveState();
      toast(CONFIG.toastCartText);
      render();

      // Apply the reached tier's discount code silently — without leaving the page.
      // Hitting /discount/<code> is the only way Shopify applies a code; fetching it
      // (rather than navigating) stores the discount on the cart so it carries through
      // to checkout. Locale-aware path. Awaited (with a timeout guard) so the cart
      // re-render below reflects the discount; best-effort if the request fails.
      if (code) {
        try {
          const ctrl = new AbortController();
          const timeoutId = setTimeout(() => ctrl.abort(), 6000);
          await fetch(ROOT + 'discount/' + encodeURIComponent(code), {
            headers: { 'Accept': 'text/html' },
            signal: ctrl.signal
          });
          clearTimeout(timeoutId);
        } catch (e) { /* best-effort — the items are in the cart regardless */ }
      }

      // Refresh the cart (count + drawer contents), then open the theme's native
      // cart drawer so the shopper sees their bundle and the applied discount —
      // no redirect to the cart page.
      refreshCartCount();
      try {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show();
        }
      } catch (e) { /* drawer is optional UX — never block the success path */ }

      allAtcBtns.forEach(btn => {
        btn.textContent = CONFIG.addedLabel;
        setTimeout(() => {
          btn.textContent = CONFIG.addToCartLabel;
        }, 2000);
      });
    } catch (err) {
      toast(CONFIG.errorCartText);
      allAtcBtns.forEach(btn => {
        btn.disabled = false;
        btn.textContent = CONFIG.addToCartLabel;
      });
    }
  }

  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count], .cart-count, .cart-pill').forEach(el => {
        el.textContent = cart.item_count;
      });
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
    } catch (e) { /* ignore */ }
  }

  if (atcBtn) {
    atcBtn.addEventListener('click', addBundleToCart);
  }

  /* ══════════════════════════════════════════════════════════════
     CLEAR BUNDLE
  ══════════════════════════════════════════════════════════════ */

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      bundle = {};
      saveState();
      render();
    });
  }

  /* ══════════════════════════════════════════════════════════════
     TOASTS
  ══════════════════════════════════════════════════════════════ */

  function toast(message) {
    if (!toastsContainer) return;

    const el = document.createElement('div');
    el.className = 'bb-toast';
    el.textContent = message;
    toastsContainer.appendChild(el);

    setTimeout(() => {
      el.classList.add('out');
      setTimeout(() => el.remove(), 250);
    }, 2500);
  }

  /* ══════════════════════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════════════════════ */

  bindCardButtons();
  render();

  if (COLLECTIONS.length > 0) {
    loadFilters(0);
  }
})();
